import { Injectable } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { first, get } from 'lodash';
import { AuthResult } from 'node_modules/angular-auth-oidc-client/lib/flows/callback-context';
import {
  filter,
  interval,
  take, takeUntil,
  tap
} from 'rxjs';
import { AbstractComponent } from '../shared/components/abstract-component';
import { IAppConfig } from '../shared/interfaces/app-config';
import { User } from '../shared/models/user';
import { AuthStore } from '../stores/auth.store';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends AbstractComponent {
  config: IAppConfig;

  constructor(
    private userService: UserService,
    private oidcSecurityService: OidcSecurityService,
    private authStore: AuthStore,
  ) {
    super();
    this.monitorToken();
  }

  monitorToken(): void {
    interval(2 * 60 * 1000)
      .pipe(
        tap(() => {
          this.checkTokenExpiry();
        })
      )
      .subscribe();
  }

  private checkTokenExpiry() {
    const authResult: AuthResult = this.authStore?.AuthResult;
    if (authResult) {
      const timeRemaining = authResult.expires_in - Date.now();

      // If token expires in less than 5 minutes (300000 ms), refresh it
      if (timeRemaining < 5 * 60 * 1000) {
        this.initAuth();
      };
    } else {
      // Initial token fetch if authResult is null
      return this.initAuth();
    }
  }

  initAuth(): void {
    this.oidcSecurityService.getAuthenticationResult().pipe(takeUntil(this.componentDestroyed))
      .subscribe((authResult: AuthResult) => {
        this.authStore.initializeCredentials(authResult)
      });
  }

  startAuthentication(): void {
    this.oidcSecurityService.authorize();
  }

  logout() {
    this.oidcSecurityService
      .logoff()
      .subscribe((result) => console.log('logout'));
  }

  completeAuthentication(): void {
    this.oidcSecurityService.checkAuth().pipe(takeUntil(this.componentDestroyed)).subscribe({
      next: ({ isAuthenticated, userData, accessToken }) => {
        if (isAuthenticated) {

          this.oidcSecurityService.getAuthenticationResult().pipe(takeUntil(this.componentDestroyed))
            .subscribe((authResult: AuthResult) => {
              this.authStore.initializeCredentials(authResult);
              this.userService.getUserInfo().pipe(
                filter((userDetails: any) => !!userDetails && !!authResult),
                take(1),
                takeUntil(this.componentDestroyed))
                .subscribe((userDetails: any) => {
                  this.authStore.initializeLogInUser(new User({
                    ...userData,
                    ...userDetails,
                    selectedRole: first(userDetails?.userRoles),
                    selectedLocation: first(get(first(userDetails?.userRoles), 'locations')),
                    firstName: userData?.profile?.given_name,
                    lastName: userData?.profile?.family_name,
                    email: userData?.profile?.EmailAddress
                  }));
                });
            });
        } else {
          this.oidcSecurityService.authorize();
        }
      },
      error: (error: any) => {
        this.oidcSecurityService.authorize();
      }
    });
  }

  override afterOnDestroy(): void {
    // afterOnDestroy
  }
}
